import { ProductStatus } from '@today/types'
import { Badge, Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ProductConfirmDialogView } from './product-confirm-dialog.view'
import { ErrorText } from './product-fields'
import type { ProductStatusView, Translate } from './product-types'

const STATUS_VARIANT: Record<ProductStatus, 'muted' | 'success' | 'secondary'> = {
	[ProductStatus.DRAFT]: 'muted',
	[ProductStatus.ACTIVE]: 'success',
	[ProductStatus.RETIRED]: 'secondary',
}

/**
 * Props for {@link ProductLifecycleView}.
 *
 * @property missingLocales - `completeness.missingLocales`; empty means publishable.
 * @property action - The one lifecycle action for the status, or null without `product.write`.
 * @property dialog - Which confirmation is open (`retire` or `delete`), if any.
 * @property onDelete - Present only with `product.delete`.
 */
export interface ProductLifecycleViewProps {
	t: Translate
	code: string
	name: string
	status: ProductStatusView
	missingLocales: string[]
	action: { id: 'publish' | 'retire' | 'reactivate'; enabled: boolean } | null
	dialog: 'retire' | 'delete' | null
	isBusy: boolean
	error: string | null
	dialogError: string | null
	onAction: () => void
	onConfirm: () => void
	onCancel: () => void
	onDelete?: () => void
}

function Completeness({ t, missing }: { t: Translate; missing: string[] }): ReactElement {
	return (
		<p data-testid="completeness" className="text-ds-sm text-ds-text-muted">
			{missing.length > 0 ? t('completeness.missing', { locales: missing.join(', ') }) : t('completeness.complete')}
		</p>
	)
}

function Dialogs({ props }: { props: ProductLifecycleViewProps }): ReactElement {
	const { t } = props
	return (
		<>
			<ProductConfirmDialogView
				t={t}
				isOpen={props.dialog === 'retire'}
				title={t('lifecycle.retireTitle')}
				body={t('lifecycle.retireBody')}
				confirmLabel={t('lifecycle.retire')}
				error={props.dialogError}
				onConfirm={props.onConfirm}
				onCancel={props.onCancel}
			/>
			<ProductConfirmDialogView
				t={t}
				isOpen={props.dialog === 'delete'}
				title={t('lifecycle.deleteTitle')}
				body={t('lifecycle.deleteBody')}
				confirmLabel={t('lifecycle.delete')}
				danger
				error={props.dialogError}
				onConfirm={props.onConfirm}
				onCancel={props.onCancel}
			/>
		</>
	)
}

/** Product header: status badge, completeness, the lifecycle action and delete (organism). */
export function ProductLifecycleView(props: ProductLifecycleViewProps): ReactElement {
	const { t, action } = props
	const blocked = action && !action.enabled && props.missingLocales.length > 0
	return (
		<header className="flex flex-col gap-2">
			<div className="flex items-center gap-3">
				<h1 className="text-ds-xl font-semibold text-ds-text">{props.name}</h1>
				<code className="text-ds-sm text-ds-text-muted">{props.code}</code>
				<Badge variant={STATUS_VARIANT[props.status]}>{t(`status.${props.status}`)}</Badge>
			</div>
			<Completeness t={t} missing={props.missingLocales} />
			<div className="flex items-center gap-2">
				{action ? (
					<Button isDisabled={!action.enabled || props.isBusy} onPress={props.onAction}>
						{t(`lifecycle.${action.id}`)}
					</Button>
				) : null}
				{blocked ? (
					<span className="text-ds-sm text-ds-danger">
						{t('lifecycle.blocked', { locales: props.missingLocales.join(', ') })}
					</span>
				) : null}
				{props.onDelete ? (
					<Button variant="danger" isDisabled={props.isBusy} onPress={props.onDelete}>
						{t('lifecycle.delete')}
					</Button>
				) : null}
			</div>
			<ErrorText message={props.error} />
			<Dialogs props={props} />
		</header>
	)
}
